import { push } from 'connected-react-router';
import envConfig from '../../../config';
import CoreAPIService from '../../../utils/core-api';
import { fetchAction } from '../../actions';
import ObjectUtils from '../../../utils/objectUtils';
import { types as billTypes } from '../bills';
import {
    BookingById,
    BookingCancelPayload,
    PriceConfirmationById,
    EtaWithGroups,
    POI,
    POIStatus,
    AvailableDispatchGroup,
} from '../api-types';
import { paths } from '../../../routes/paths';
export type { BookingById };

export const name: string = 'booking';

export const types = {
    GET_BOOKING_RIDE_MANAGER: name + '/GET_BOOKING_RIDE_MANAGER',
    GET_RIDE_POSITIONS_HISTORY: name + '/GET_RIDE_POSITIONS_HISTORY',
    GET_BOOKING: name + '/GET_BOOKING',
    CANCEL_BOOKING: name + '/CANCEL_BOOKING',
    CANCEL_BOOKING_CALL_CENTER: name + '/CANCEL_BOOKING_CALL_CENTER',
    CREATE_PRICE_CONFIRMATION: name + '/CREATE_PRICE_CONFIRMATION',
    CONFIRM_PRICE_CONFIRMATION: name + '/CONFIRM_PRICE_CONFIRMATION',
    CLEAR_PRICE_CONFIRMATION: name + '/CLEAR_PRICE_CONFIRMATION',
    CLEAR_PU: name + '/CLEAR_PU',
    CLEAR_RESERVER: name + '/CLEAR_RESERVER',
    GET_AVAILABLE_GROUPS: name + '/GET_AVAILABLE_GROUPS',
    GET_ETA_WITH_GROUPS: name + '/GET_ETA_WITH_GROUPS',
    ADD_TOLL_FEE: name + '/ADD_TOLL_FEE',
    SAVE_REFOUND: name + '/SAVE_REFOUND',
    RECALCULATE_REFOUND: name + '/RECALCULATE_REFOUND',
    GET_POI: name + '/GET_POI',
    CHECK_FLIGHT: name + '/CHECK_FLIGHT',
    CLEAR_FLIGHT: name + '/CLEAR_FLIGHT',
};

interface BookingsState {
    bookings: BookingById;
    poi: POI;
    poiStatus: POIStatus;
    etaWithGroups: EtaWithGroups;
    availableGroups: AvailableDispatchGroup[];
    priceConfirmation: PriceConfirmationById;
}

const initialState: BookingsState = {
    bookings: {} as BookingById,
    poi: {} as POI,
    poiStatus: {} as POIStatus,
    etaWithGroups: {} as EtaWithGroups,
    availableGroups: [],
    priceConfirmation: {} as PriceConfirmationById,
};

const reducer = (state = initialState, action): BookingsState => {
    const payload = action.payload;
    const parameters = action.parameters;
    const id = parameters ? parameters.id : 0;
    const bill_id = parameters ? parameters.bill_id : 0;
    const booking_id = parameters ? parameters.booking_id : 0;
    const price = parameters ? parameters.price : 0;

    switch (action.type) {
        case types.GET_BOOKING_RIDE_MANAGER:
            // Extract cancel_reason_rider from RideManager payload
            const cancelReasonRider = payload?.cancel_reason_rider;

            // Find the existing booking by UUID and add cancel_reason_rider to it
            if (cancelReasonRider) {
                const bookingEntries = Object.entries(state.bookings);
                const foundBooking = bookingEntries.find(([key, booking]) => booking.ride_uuid === id);

                if (foundBooking) {
                    const [bookingKey, booking] = foundBooking;
                    return {
                        ...state,
                        bookings: {
                            ...state.bookings,
                            [bookingKey]: {
                                ...booking,
                                cancel_reason_rider: cancelReasonRider,
                            },
                        },
                    };
                }
            }
            return state;
        case types.GET_BOOKING:
        case types.CANCEL_BOOKING:
            return {
                ...state,
                bookings: { ...state.bookings, [id]: payload },
            };
        case types.CREATE_PRICE_CONFIRMATION:
            return {
                ...state,
                priceConfirmation: payload,
            };
        case types.CONFIRM_PRICE_CONFIRMATION:
            return state;
        case types.CLEAR_PRICE_CONFIRMATION:
            return {
                ...state,
                priceConfirmation: {},
            };
        case types.CLEAR_PU:
            return {
                ...state,
                poi: {} as POI,
                poiStatus: {} as POIStatus,
                etaWithGroups: {} as EtaWithGroups,
                availableGroups: [],
            };
        case types.CLEAR_RESERVER:
            return {
                ...state,
                poi: {} as POI,
                poiStatus: {} as POIStatus,
                etaWithGroups: {} as EtaWithGroups,
                availableGroups: [],
                priceConfirmation: {},
            };
        case types.GET_POI:
            return {
                ...state,
                poi: payload?.length > 0 ? payload[0] : {},
            };
        case types.CHECK_FLIGHT:
            return {
                ...state,
                poiStatus: payload,
            };
        case types.CLEAR_FLIGHT:
            return {
                ...state,
                poiStatus: {} as POIStatus,
            };
        case types.GET_ETA_WITH_GROUPS:
            return {
                ...state,
                etaWithGroups: payload,
            };
        case types.GET_AVAILABLE_GROUPS:
            return {
                ...state,
                availableGroups: payload,
            };
        case types.ADD_TOLL_FEE:
            const newState = ObjectUtils.deepCopy(state);
            const bill = newState.bookings[id]?.bills?.find((b) => b.id === bill_id);
            bill.lines.push({
                name: 'fare_breakdown.toll_fee',
                amount_with_vat: price,
            });
            return newState;
        case billTypes.SIMPLE_BILLS_RECOVER:
            if (bill_id > 0) {
                const newState = ObjectUtils.deepCopy(state);
                const bill = newState.bookings[booking_id]?.bills?.find((b) => b.id === bill_id);
                if (bill) {
                    bill.paid = payload.paid;
                    bill.transaction = payload.transaction;
                }
                return newState;
            }
            return state;
        case types.SAVE_REFOUND:
            if (ObjectUtils.isEmpty(payload)) {
                return state;
            }
            return {
                ...state,
                bookings: {
                    ...state.bookings,
                    [id]: {
                        ...state.bookings[id],
                        regulations_billing: [...state.bookings[id].regulations_billing, payload.data?.[0]],
                    },
                },
            };
        default:
            return state;
    }
};
export default reducer;

export const actions = {
    getBookingRideManager: (id) =>
        fetchAction(CoreAPIService.get(`/rideManager/rides/${id}`), types.GET_BOOKING_RIDE_MANAGER, { id }),
    // Fetch the GPS positions history for a ride from the whereru service (speed in m/s).
    // The result is delivered through the success callback; nothing is persisted in the store.
    getRidePositionsHistory: (uuid, callback?) =>
        fetchAction(
            CoreAPIService.get(`whereru/positions/history/ride/${uuid}`, null, null, envConfig.privapi_url),
            types.GET_RIDE_POSITIONS_HISTORY,
            { id: uuid },
            {
                success: (_dispatch, response) => callback?.(response?.payload),
                // On failure, invoke the callback with no payload so the caller falls back
                // to the legacy point source instead of waiting forever.
                error: () => callback?.(null),
            },
        ),
    getBooking: (id, callback?) =>
        fetchAction(
            CoreAPIService.get(`internal/backoffice/bookings/${id}`, null, null, envConfig.privapi_url),
            types.GET_BOOKING,
            { id },
            { success: callback },
        ),
    cancelBooking: (id, payload: BookingCancelPayload = {}, callback?) =>
        fetchAction(
            CoreAPIService.post(`/core/bookings/${id}/cancel`, payload),
            types.CANCEL_BOOKING,
            { id },
            { success: callback },
        ),
    cancelBookingCallCenter: (payload: { uuid: string; phonenumber: string }, callback?) =>
        fetchAction(
            CoreAPIService.delete(`/core/ride`, { ...payload, isBooking: true }),
            types.CANCEL_BOOKING_CALL_CENTER,
            {},
            { success: callback },
        ),
    createPriceConfirmation: (payload) =>
        fetchAction(CoreAPIService.post(`/core/orders`, payload), types.CREATE_PRICE_CONFIRMATION, {}),
    confirmPriceConfirmation: (id, payload, callback?) =>
        fetchAction(
            CoreAPIService.put(`/core/orders/${id}/confirm`, payload),
            types.CONFIRM_PRICE_CONFIRMATION,
            {},
            {
                success: (dispatch, response) => {
                    if (callback) {
                        callback(response.payload.content);
                    }
                },
            },
        ),
    clearReserver: () => ({
        type: types.CLEAR_RESERVER,
        payload: {},
    }),
    clearPriceConfirmation: () => ({
        type: types.CLEAR_PRICE_CONFIRMATION,
        payload: {},
    }),
    clearPickUp: () => ({
        type: types.CLEAR_PU,
        payload: {},
    }),
    getAvailableGroups: (payload: { lat: number; lng: number; profile: string; rider: number; date?: number }) =>
        fetchAction(CoreAPIService.get(`/core/dispatch_groups/available`, payload), types.GET_AVAILABLE_GROUPS, {}),
    getEtaWithGroups: (payload: { lat: number; lng: number }) =>
        fetchAction(
            CoreAPIService.get(`/core/vehicles/eta_with_groups?lat=${payload.lat}&lng=${payload.lng}`),
            types.GET_ETA_WITH_GROUPS,
            {},
        ),
    getPoi: (payload: { lat: number; lng: number }, callback?) =>
        fetchAction(
            CoreAPIService.get(`/core/poi?lat=${payload.lat}&lng=${payload.lng}`),
            types.GET_POI,
            {},
            {
                success: (dispatch, response) => {
                    if (callback) {
                        callback(response.payload);
                    }
                },
            },
        ),
    checkFlight: (poiID, carrierCodeAndFlightNumber, time) =>
        fetchAction(
            CoreAPIService.get(`/core/info/flights/${poiID}/${carrierCodeAndFlightNumber}?time=${time}`),
            types.CHECK_FLIGHT,
            {},
        ),
    clearFlight: () => ({
        type: types.CLEAR_FLIGHT,
        payload: {},
    }),
    addTollFee: (id, payload) =>
        fetchAction(CoreAPIService.post(`/core/regulations/toll`, payload), types.ADD_TOLL_FEE, {
            id: payload.booking_id,
            bill_id: id,
            price: payload.amount,
        }),
    saveRefound: (id, payload) =>
        fetchAction(
            CoreAPIService.post(`/billing/adjustments`, payload), // old => /core/regulations
            types.SAVE_REFOUND,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.bookings}/${id}`));
                },
            },
        ),
    recalculateRefound: (id, payload, callback) =>
        fetchAction(
            CoreAPIService.post(`/core/bookings/${id}/price/recalculate`, { pricing_params: payload }),
            types.RECALCULATE_REFOUND,
            { id },
            {
                success: (dispatch, response) => {
                    if (callback) {
                        callback(response.payload);
                    }
                },
            },
        ),
};

const localState = (state: any): BookingsState => state[name] || initialState;
export const selectors = {
    bookings: (state) => localState(state).bookings,
    poi: (state) => localState(state).poi,
    poiStatus: (state) => localState(state).poiStatus,
    etaWithGroups: (state) => localState(state).etaWithGroups,
    availableGroups: (state) => localState(state).availableGroups,
    priceConfirmation: (state) => localState(state).priceConfirmation,
};
